import { z } from 'zod'
import { oc } from '@orpc/contract'

/**
 * Publish a provider's bookable slots to Zocdoc from a weekly working-hours pattern.
 *
 * Wraps `PUT /v1/providers/{provider_id}/calendar/timeslots?date=…`, whose semantics drive this
 * whole contract (Zocdoc OpenAPI v1.177):
 *
 * - **one request per date**, and each one REPLACES that provider's entire slot list for the date
 *   across every location — an empty array deletes the day;
 * - each slot needs `provider_id`, `location_id`, a **local** `start_time` whose date part equals
 *   the `date` parameter, and an IANA `time_zone`;
 * - at most **1500** slots per request, which cannot be paginated: a second PUT for the same date
 *   overwrites the first rather than adding to it;
 * - a `200` may still carry `errors.not_found_location_ids`, so success is not the status alone;
 * - the slot store is **eventually consistent** — a read immediately after a write may not show it.
 *
 * The caller identifies the provider by **NPI**, not Zocdoc's `pr_…` id: the NPI is the identifier
 * beHuman already holds on a Practitioner, and Zocdoc's calendar endpoint rejects it (verified —
 * `provider_id` invalid), so resolving NPI → `pr_…` + location ids is the wrapper's whole point.
 *
 * No FHIR at the boundary: the practitioner crosses as a bare NPI string and nothing is read from
 * or written to Medplum.
 */

/** 0 = Sunday … 6 = Saturday, matching `Date.getUTCDay()`. */
export const WEEKDAY_VALUES = [0, 1, 2, 3, 4, 5, 6] as const
export type Weekday = (typeof WEEKDAY_VALUES)[number]

export const PATIENT_TYPE_VALUES = ['new', 'existing'] as const
export type ZocdocPatientType = (typeof PATIENT_TYPE_VALUES)[number]

/** Zocdoc's cap on one request's `timeslots` array. */
export const ZOCDOC_MAX_TIMESLOTS_PER_REQUEST = 1500

const dayPattern = /^\d{4}-\d{2}-\d{2}$/
const timePattern = /^([01]\d|2[0-3]):[0-5]\d$/

export const zocdocSetAvailability = oc
  .route({ method: 'POST', path: '/v2/zocdoc/availability' })
  .input(
    z.object({
      /** The practitioner's NPI. Resolved to Zocdoc's `pr_…` id inside the handler. */
      npi: z.string().min(1),
      /** Inclusive `YYYY-MM-DD` window the weekly pattern repeats across. */
      fromDay: z.string().regex(dayPattern),
      toDay: z.string().regex(dayPattern),
      weekdays: z
        .array(
          z.union([
            z.literal(0),
            z.literal(1),
            z.literal(2),
            z.literal(3),
            z.literal(4),
            z.literal(5),
            z.literal(6),
          ]),
        )
        .min(1),
      /** Local wall clock `HH:mm` in `timeZone`. */
      startTime: z.string().regex(timePattern),
      endTime: z.string().regex(timePattern),
      /** Spacing between slot starts. Zocdoc derives the *duration* from the visit reason. */
      slotMinutes: z.number().int().min(5).max(240),
      /**
       * IANA zone the wall-clock times are in. Omitted = the zone Zocdoc reports for the
       * provider's schedulable entity, which is the only value Zocdoc itself vouches for.
       */
      timeZone: z.string().optional(),
      /** Restrict to these visit reasons. Omitted or empty = unrestricted, per Zocdoc. */
      allowedVisitReasonIds: z.array(z.string()).optional(),
      excludedVisitReasonIds: z.array(z.string()).optional(),
      patientType: z.enum(PATIENT_TYPE_VALUES).optional(),
      /** Publish only these `pr_…|lo_…` pairs. Omitted = every schedulable pair the provider has. */
      entityIds: z.array(z.string()).optional(),
      /**
       * Publish only at these locations, as the bare `lo_…` ids Zocdoc's directory lists (a
       * composite `pr_…|lo_…` is accepted too and read for its location half). Omitted or empty =
       * every location. Intersected with `entityIds` when both are given.
       *
       * A subset does **not** protect the locations left out: the underlying PUT replaces the
       * provider's whole slot list for each date, across every location. So publishing for
       * telehealth alone clears that date's in-person slots as well — which is exactly why
       * `unknownLocationIds` below is reported rather than silently ignored.
       */
      locationIds: z.array(z.string()).optional(),
      /**
       * Build and validate without sending anything. The default, because each PUT destroys a
       * day's existing availability — publishing must be asked for explicitly.
       */
      dryRun: z.boolean().optional(),
    }),
  )
  .output(
    z.object({
      dryRun: z.boolean(),
      /** Resolved from the NPI. */
      providerId: z.string().optional(),
      /** The `pr_…|lo_…` pairs the slots were built for. */
      entityIds: z.array(z.string()),
      /**
       * Requested `locationIds` that match no schedulable pair for this provider. Reported so a
       * stale id in the caller's list does not quietly shrink the publish to the ids that happen
       * to still be valid.
       */
      unknownLocationIds: z.array(z.string()).optional(),
      timeZone: z.string().optional(),
      totalSlots: z.number(),
      /** One entry per date, in date order. Dates are NOT written atomically. */
      dates: z.array(
        z.object({
          date: z.string(),
          slotCount: z.number(),
          /** False on a dry run — nothing was sent. */
          sent: z.boolean(),
          ok: z.boolean(),
          /** Locations Zocdoc did not recognise, even on a 200. */
          notFoundLocationIds: z.array(z.string()).optional(),
          error: z.string().optional(),
        }),
      ),
      /**
       * Dates whose slot count exceeds Zocdoc's 1500 cap. Reported rather than split: a second PUT
       * for the same date would replace the first, so an over-cap date cannot be paginated.
       */
      overCap: z.array(z.object({ date: z.string(), slotCount: z.number() })),
      error: z.string().optional(),
      missingEnv: z.array(z.string()).optional(),
    }),
  )

/** Days per request. Zocdoc requires a `date`, so a range costs one call per day. */
export const ZOCDOC_MAX_SLOT_DAYS = 31

/**
 * Read the slots already on a provider's Zocdoc calendar, by NPI
 * (`GET /v1/providers/{provider_id}/calendar/timeslots?date=…`).
 *
 * The counterpart to `zocdocSetAvailability`, and the honest way to confirm a publish landed. Two
 * properties of the upstream read shape this contract:
 *
 * - **`date` is required**, so a range is one request per day — hence the `ZOCDOC_MAX_SLOT_DAYS`
 *   bound rather than an open-ended window;
 * - the slot store is **eventually consistent**, so a read immediately after publishing may come
 *   back short. `staleWarning` says so rather than letting an empty day look like a failed write.
 */
export const zocdocProviderSlots = oc
  .route({ method: 'GET', path: '/v2/zocdoc/availability' })
  .input(
    z.object({
      npi: z.string().min(1),
      /** Inclusive `YYYY-MM-DD` window, at most `ZOCDOC_MAX_SLOT_DAYS` days wide. */
      fromDay: z.string().regex(dayPattern),
      toDay: z.string().regex(dayPattern),
      /**
       * The reason availability is judged against. Defaults to the provider's own
       * `default_visit_reason_id`.
       *
       * It has to be *a* reason because Zocdoc computes availability per reason — a slot
       * restricted by `allowed_visit_reason_ids` is legitimately unbookable for a reason it does
       * not accept, and there is no reason-independent notion of "free".
       */
      visitReasonId: z.string().optional(),
      /** New-patient availability can differ from existing-patient availability. */
      patientType: z.enum(PATIENT_TYPE_VALUES).default('new'),
    }),
  )
  .output(
    z.object({
      providerId: z.string().optional(),
      totalSlots: z.number(),
      /** One entry per day in the window, in date order, including days with no slots. */
      days: z.array(
        z.object({
          date: z.string(),
          slots: z.array(
            z.object({
              timeslotId: z.string(),
              /** Local date-time, no offset — read alongside `timeZone`. */
              startTime: z.string().optional(),
              timeZone: z.string().optional(),
              locationId: z.string().optional(),
              /**
               * The `locationId` decoded against `locations`, so a caller never has to join the
               * two id shapes itself (a slot carries a bare `lo_…`, a location the composite
               * `pr_…|lo_…`). **Absent means undecodable, not virtual** — Zocdoc described no
               * location with that id, and the id is then listed in `unresolvedLocationIds`.
               */
              location: z.object({ name: z.string().optional(), virtual: z.boolean() }).optional(),
              patientType: z.enum(PATIENT_TYPE_VALUES).optional(),
              /**
               * The visit reasons this slot accepts. Absent or empty = unrestricted, per Zocdoc.
               * Booking with a reason outside this set is what Zocdoc rejects, so a booking UI
               * must offer only these.
               */
              allowedVisitReasonIds: z.array(z.string()).optional(),
              /** Reasons explicitly barred, which override `allowedVisitReasonIds`. */
              excludedVisitReasonIds: z.array(z.string()).optional(),
              createdUtc: z.string().optional(),
            }),
          ),
          /** Set when that day's read failed; its slots are then unknown, not empty. */
          error: z.string().optional(),
        }),
      ),
      /**
       * Every location Zocdoc describes for this provider — virtual ones included — keyed by the
       * bare `lo_…` the slots carry. Free of charge: it comes off the same `/v1/providers` read
       * that resolves the NPI, so no extra request pays for it.
       *
       * Present whether or not any slot uses a location, which is what lets a caller label an
       * empty virtual/on-site view with the location it *would* have shown.
       */
      locations: z.array(
        z.object({
          locationId: z.string(),
          name: z.string().optional(),
          address: z.string().optional(),
          city: z.string().optional(),
          state: z.string().optional(),
          timeZone: z.string().optional(),
          /** Telehealth rather than a physical address — Zocdoc's `virtual_locations` entries. */
          virtual: z.boolean(),
        }),
      ),
      /**
       * Location ids that appeared on a slot but matched nothing in `locations`. Reported rather
       * than dropped: silently hiding those slots would make a shape mismatch look like a
       * provider with a clear calendar, the same trap `days[].error` already avoids.
       */
      unresolvedLocationIds: z.array(z.string()),
      /**
       * Published slots dropped because Zocdoc does not offer them for booking — almost always
       * because they are already booked.
       *
       * Counted rather than silently omitted: a shorter list than the calendar holds is exactly
       * the thing an operator would otherwise mistake for a failed publication.
       */
      bookedSlots: z.number().optional(),
      /** What "free" was judged against, so a caller can say which reason the answer is for. */
      availabilityCheck: z
        .object({
          visitReasonId: z.string(),
          patientType: z.enum(PATIENT_TYPE_VALUES),
        })
        .optional(),
      /**
       * Set when availability could not be read, or when the provider offers no visit reason to
       * judge against. **The slots then include booked ones** — they are what the calendar holds,
       * not what can be booked — and a caller must say so rather than presenting them as free.
       */
      availabilityError: z.string().optional(),
      staleWarning: z.string().optional(),
      error: z.string().optional(),
      missingEnv: z.array(z.string()).optional(),
    }),
  )
